package br.com.htms.inspeccaocampo.ui.dashboards

import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import br.com.htms.inspeccaocampo.data.funcionarios.DocumentoResumo
import br.com.htms.inspeccaocampo.data.funcionarios.DocumentoTipo
import br.com.htms.inspeccaocampo.data.funcionarios.Funcionario
import br.com.htms.inspeccaocampo.data.funcionarios.FuncionariosApi
import br.com.htms.inspeccaocampo.ui.kit.BarrasPercentual
import br.com.htms.inspeccaocampo.ui.kit.CoresDash
import br.com.htms.inspeccaocampo.ui.kit.DashDatas
import br.com.htms.inspeccaocampo.ui.kit.FiltroPeriodo
import br.com.htms.inspeccaocampo.ui.kit.FiltrosAtivos
import br.com.htms.inspeccaocampo.ui.kit.FmtDash
import br.com.htms.inspeccaocampo.ui.kit.GradeKpis
import br.com.htms.inspeccaocampo.ui.kit.GraficoColunas
import br.com.htms.inspeccaocampo.ui.kit.GraficoRosca
import br.com.htms.inspeccaocampo.ui.kit.ItemPainel
import br.com.htms.inspeccaocampo.ui.kit.KpiDash
import br.com.htms.inspeccaocampo.ui.kit.PainelDashboard
import br.com.htms.inspeccaocampo.ui.kit.PaineisDoDash
import br.com.htms.inspeccaocampo.ui.kit.PeriodoDash
import br.com.htms.inspeccaocampo.ui.kit.RankingBarras
import br.com.htms.inspeccaocampo.ui.kit.SemDadosDash

class DadosDocumentos(val funcionarios: List<Funcionario>, val documentos: List<DocumentoResumo>)

suspend fun carregarDadosDocumentos(): Result<DadosDocumentos> = runCatching {
    val f = FuncionariosApi.listar().getOrThrow()
    val d = FuncionariosApi.resumoDocumentos().getOrThrow()
    DadosDocumentos(f, d)
}

private val TiposCobertura = listOf("OS", "FICHA_EPI", "ASO", "TREINAMENTO", "CERTIFICADO")

@Composable
fun DashboardDocumentosCarregando(chave: Any = Unit) {
    CarregadorDash("Emissão de Documentos", chave, { carregarDadosDocumentos() }) { d, recarregar, carregando ->
        DashboardDocumentos(d, carregando, recarregar)
    }
}

@Composable
fun DashboardDocumentos(d: DadosDocumentos, carregando: Boolean = false, onAtualizar: (() -> Unit)? = null) {
    var periodo by remember { mutableStateOf(PeriodoDash.M12) }
    var fTipo by remember { mutableStateOf<String?>(null) }
    var fEmissor by remember { mutableStateOf<String?>(null) }
    var fFaixa by remember { mutableStateOf<Int?>(null) }

    val hoje = DashDatas.hoje()
    fun dataDe(x: DocumentoResumo) = DashDatas.deMs(x.createdAt)
    fun tipoDe(x: DocumentoResumo) = DocumentoTipo.rotulo(x.tipo)
    fun emissorDe(x: DocumentoResumo) = if (x.origem == "EMITIDO") x.createdBy.ifBlank { "Portal" } else "Anexado manualmente"

    val porFunc = d.funcionarios.associateBy { it.id }
    val ativos = d.funcionarios.filter { it.situacao.isBlank() || it.situacao.trim().uppercase().startsWith("ATIVO") }
    val noPeriodo = d.documentos.filter { it.createdAt > 0 && DashDatas.noPeriodo(dataDe(it), periodo, hoje) }
    val faixas = remember(periodo, d) { DashDatas.faixas(periodo, d.documentos.filter { it.createdAt > 0 }.minOfOrNull { dataDe(it) }, hoje) }
    val semFaixa = noPeriodo.filter { (fTipo == null || tipoDe(it) == fTipo) && (fEmissor == null || emissorDe(it) == fEmissor) }
    val lista = semFaixa.filter { fFaixa == null || DashDatas.indice(faixas, dataDe(it)) == fFaixa }
    val cores = coresRanking(DocumentoTipo.todos.map { it.second })
    val tiposPorFunc = d.documentos.groupBy { it.funcionarioId }.mapValues { (_, v) -> v.map { it.tipo }.toSet() }
    val semOs = ativos.count { "OS" !in tiposPorFunc[it.id].orEmpty() }
    val semEpi = ativos.count { "FICHA_EPI" !in tiposPorFunc[it.id].orEmpty() }

    val filtrosAtivos = buildList {
        fFaixa?.let { i -> faixas.getOrNull(i)?.let { add("Período: ${it.rotulo}" to { fFaixa = null }) } }
        fTipo?.let { add("Tipo: $it" to { fTipo = null }) }
        fEmissor?.let { add("Emissor: $it" to { fEmissor = null }) }
    }

    PainelDashboard(
        titulo = "Dashboard — Emissão de Documentos",
        subtitulo = "OS, Ficha de EPI, ASO, treinamentos, certificados e demais documentos no cadastro",
        carregando = carregando,
        onAtualizar = onAtualizar,
        camposCor = DocumentoTipo.todos.mapIndexed { i, (_, nome) -> nome to CoresDash.corLong(i) },
        filtros = {
            FiltroPeriodo(periodo, { periodo = it; fFaixa = null })
            FiltrosAtivos(filtrosAtivos) { fTipo = null; fEmissor = null; fFaixa = null }
        }
    ) { largo, grande ->
        if (d.funcionarios.isEmpty() && d.documentos.isEmpty()) {
            SemDadosDash("Sem documentos", "Nenhum funcionário ou documento cadastrado.")
            return@PainelDashboard
        }
        PaineisDoDash("documentos", largo, organizar = !grande, itens = listOf(
            ItemPainel("kpis", "Indicadores", inteiro = true, conteudo = {
                GradeKpis(
            largo,
            listOf(
                { m -> KpiDash("Documentos no período", FmtDash.qtd(lista.size), m, legenda = "${lista.count { it.origem == "EMITIDO" }} emitido(s) pelo portal") },
                { m -> KpiDash("Ordens de Serviço", FmtDash.qtd(lista.count { it.tipo == "OS" }), m, cor = cores["Ordem de Serviço"] ?: CoresDash.cor(0)) },
                { m -> KpiDash("Fichas de EPI", FmtDash.qtd(lista.count { it.tipo == "FICHA_EPI" }), m, cor = cores["Ficha de EPI"] ?: CoresDash.cor(4)) },
                { m -> KpiDash("ASO", FmtDash.qtd(lista.count { it.tipo == "ASO" }), m, cor = cores["ASO"] ?: CoresDash.cor(1)) },
                { m -> KpiDash("Treinamentos", FmtDash.qtd(lista.count { it.tipo == "TREINAMENTO" }), m, cor = cores["Treinamento"] ?: CoresDash.cor(2)) },
                { m -> KpiDash("Certificados", FmtDash.qtd(lista.count { it.tipo == "CERTIFICADO" }), m, cor = cores["Certificado"] ?: CoresDash.cor(3)) },
                { m -> KpiDash("Funcionários ativos", FmtDash.qtd(ativos.size), m, legenda = "${d.funcionarios.size} no cadastro") },
                { m -> KpiDash("Ativos sem OS", FmtDash.qtd(semOs), m, cor = if (semOs > 0) CoresDash.ruim else CoresDash.bom, destaque = semOs > 0, legenda = "sem Ficha de EPI: $semEpi") }
            )
        )
            }),
            ItemPainel("periodo", "Documentos por período", inteiro = true) { m ->
                GraficoColunas(
                    "Documentos por período", "Empilhados por tipo · clique numa coluna para filtrar",
                    faixas.map { it.rotulo },
                    seriesPorChave(semFaixa, faixas, ::dataDe, ::tipoDe, cores, top = 6),
                    m, grande, fmt = { FmtDash.qtd(it) },
                    selecionada = fFaixa, onClick = { fFaixa = if (fFaixa == it) null else it }
                )
            },
            ItemPainel("tipo", "Por tipo") { m ->
                GraficoRosca("Por tipo", "Clique para filtrar", fatias(contagem(lista, ::tipoDe), cores), m, grande, rotuloTotal = "documentos", selecionada = fTipo, onClick = { fTipo = alternar(fTipo, it) })
            },
            ItemPainel("cobertura", "Cobertura dos funcionários") { m ->
                BarrasPercentual(
                    "Cobertura dos funcionários ativos", "Quem já tem ao menos um documento de cada tipo",
                    TiposCobertura.map { t ->
                        val n = ativos.count { t in tiposPorFunc[it.id].orEmpty() }
                        Triple(DocumentoTipo.rotulo(t), if (ativos.isEmpty()) 0.0 else n.toDouble() / ativos.size, "$n de ${ativos.size}")
                    },
                    m
                )
            },
            ItemPainel("emissor", "Por emissor") { m ->
                RankingBarras("Por emissor", "Quem gerou os documentos · clique para filtrar", contagem(lista, ::emissorDe), m, cor = CoresDash.cor(1), selecionado = fEmissor, onClick = { fEmissor = alternar(fEmissor, it) })
            },
            ItemPainel("cargo", "Por cargo") { m ->
                RankingBarras(
                    "Por cargo", "Documentos no período por cargo do funcionário",
                    contagem(lista) { porFunc[it.funcionarioId]?.cargoNome?.ifBlank { null } ?: "Sem cargo" }, m, cor = CoresDash.cor(5)
                )
            }
        ))
    }
}
